import React, { useCallback, useEffect, useState } from 'react'
import { useToast } from 'hooks/toast'
import { useLoading } from 'hooks/loading'
import api from 'services/api'
import { Loading } from 'components/Loading'
import {
  currencyMask,
  genericMaskWithTwoZero,
  convertValueMaskInNumberWithTwoZeroDigits
} from 'utlis/mask'

interface IPlanFrequency {
  id: number
  frequency: string
  payament_form: string
}

interface IMenuFoodOption {
  id: number
  menu_food_type?: {
    id: number
    name: string
  }
  plans_frequency: IPlanFrequency[]
}

interface IMenuFoodRow {
  menu_food_id: string
  plan_frequency_id: string
  quantity: string
  value: string
  observation: string
}

interface IFormAddFinancialMovimentProps {
  institution_id: number
  isOpenInModal: {
    handleOnClose: () => void
  }
}

const PAYMENT_METHODS = [
  { value: 'P', name: 'Pix' },
  { value: 'D', name: 'Dinheiro' },
  { value: 'B', name: 'Boleto' }
]

const parsePayamentForm = (
  payament_form: string
): { periodicity: string; value: number } => {
  try {
    return JSON.parse(payament_form)
  } catch {
    return { periodicity: '', value: 0 }
  }
}

const FormAddFinancialMoviment = ({
  institution_id,
  isOpenInModal
}: IFormAddFinancialMovimentProps) => {
  const { addToast } = useToast()
  const { activeLoading, disableLoading } = useLoading()
  const [loading, setIsLoading] = useState(false)
  const [menuFoodOptions, setMenuFoodOptions] = useState<IMenuFoodOption[]>([])
  const [rows, setRows] = useState<IMenuFoodRow[]>([
    {
      menu_food_id: '',
      plan_frequency_id: '',
      quantity: '',
      value: '',
      observation: ''
    }
  ])
  const [paymentMethod, setPaymentMethod] = useState('')
  const [settlementDate, setSettlementDate] = useState('')

  const handleSettlementDateChange = (value: string) => {
    let v = value.replace(/\D/g, '')
    if (v.length > 8) v = v.slice(0, 8)
    if (v.length > 4) {
      v = v.replace(/(\d{2})(\d{2})(\d{1,4})/, '$1/$2/$3')
    } else if (v.length > 2) {
      v = v.replace(/(\d{2})(\d{1,2})/, '$1/$2')
    }
    setSettlementDate(v)
  }

  useEffect(() => {
    api
      .get(
        `/commercial/menuFoods/listByInstitution?institution_id=${institution_id}`
      )
      .then(res => {
        setMenuFoodOptions(res.data?.data || res.data)
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro ao carregar cardápios',
          description: 'Houve um erro ao carregar os cardápios da instituição.'
        })
      })
  }, [addToast, institution_id])

  const handleAddRow = () => {
    setRows(prev => [
      ...prev,
      {
        menu_food_id: '',
        plan_frequency_id: '',
        quantity: '',
        value: '',
        observation: ''
      }
    ])
  }

  const handleRemoveRow = (index: number) => {
    setRows(prev => prev.filter((_, i) => i !== index))
  }

  const handleRowChange = (
    index: number,
    field: keyof IMenuFoodRow,
    value: string
  ) => {
    setRows(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }
      if (field === 'menu_food_id') {
        updated[index].plan_frequency_id = ''
        updated[index].value = ''
      }
      if (field === 'plan_frequency_id') {
        const menuFood = menuFoodOptions.find(
          mf => String(mf.id) === updated[index].menu_food_id
        )
        const planFreq = menuFood?.plans_frequency.find(
          pf => String(pf.id) === value
        )
        if (planFreq) {
          const { value: planValue } = parsePayamentForm(planFreq.payament_form)
          updated[index].value = genericMaskWithTwoZero(
            String(Math.round(planValue * 100))
          )
        } else {
          updated[index].value = ''
        }
      }
      return updated
    })
  }

  const calculateTotal = useCallback(() => {
    return rows.reduce((total, row) => {
      const value = convertValueMaskInNumberWithTwoZeroDigits(row.value)
      const quantity = Number(row.quantity) || 0
      return total + value * quantity
    }, 0)
  }, [rows])

  const handleSubmit = async () => {
    const validRows = rows.filter(
      r => r.menu_food_id && r.plan_frequency_id && Number(r.quantity) > 0
    )
    if (validRows.length === 0) {
      addToast({
        type: 'error',
        title: 'Preencha ao menos um cardápio com periodicidade e quantidade'
      })
      return
    }
    if (!paymentMethod) {
      addToast({
        type: 'error',
        title: 'Selecione o método de pagamento'
      })
      return
    }
    if (!settlementDate || settlementDate.length !== 10) {
      addToast({
        type: 'error',
        title: 'Informe a data de liquidação no formato dd/mm/aaaa'
      })
      return
    }

    setIsLoading(true)
    activeLoading()
    try {
      const items = validRows.map(r => {
        const menuFood = menuFoodOptions.find(
          mf => String(mf.id) === r.menu_food_id
        )
        return {
          menu_food_id: Number(r.menu_food_id),
          menu_food_type_id: menuFood?.menu_food_type?.id,
          name: menuFood?.menu_food_type?.name,
          plan_frequency_id: Number(r.plan_frequency_id),
          quantity: Number(r.quantity),
          value: convertValueMaskInNumberWithTwoZeroDigits(r.value),
          observation: r.observation || undefined
        }
      })
      const [dd, mm, yyyy] = settlementDate.split('/')
      await api.post('/financial/financialMoviments/createByInstitution', {
        institution_id,
        payment_method: paymentMethod,
        settlement_date: `${yyyy}-${mm}-${dd}`,
        total_value: calculateTotal(),
        items
      })
      disableLoading()
      setIsLoading(false)
      addToast({
        type: 'success',
        title: 'Movimentação financeira criada com sucesso'
      })
      isOpenInModal.handleOnClose()
    } catch (error: any) {
      disableLoading()
      setIsLoading(false)
      const message = error?.response?.data?.message
      addToast({
        type: 'error',
        title: 'Erro ao criar movimentação financeira',
        description:
          message ||
          'Houve um erro ao criar a movimentação financeira, tente novamente mais tarde!'
      })
    }
  }

  return (
    <>
      <Loading isActive={loading} />
      <div>
        <table className="table table-striped table-row-bordered gs-3 border table-hover">
          <thead>
            <tr>
              <th>Cardápio</th>
              <th>Periodicidade</th>
              <th>Valor (R$)</th>
              <th>Quantidade</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const selectedMenuFood = menuFoodOptions.find(
                mf => String(mf.id) === row.menu_food_id
              )
              const planFrequencies = selectedMenuFood?.plans_frequency || []

              return (
                <React.Fragment key={index}>
                  <tr>
                    <td>
                      <select
                        className="form-select form-select-solid fw-bold"
                        value={row.menu_food_id}
                        onChange={e =>
                          handleRowChange(index, 'menu_food_id', e.target.value)
                        }
                      >
                        <option value="" disabled>
                          Selecione
                        </option>
                        {menuFoodOptions.map(mf => (
                          <option key={mf.id} value={mf.id}>
                            {mf.menu_food_type?.name || `Cardápio ${mf.id}`}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="form-select form-select-solid fw-bold"
                        value={row.plan_frequency_id}
                        onChange={e =>
                          handleRowChange(
                            index,
                            'plan_frequency_id',
                            e.target.value
                          )
                        }
                        disabled={
                          !row.menu_food_id || planFrequencies.length === 0
                        }
                      >
                        <option value="" disabled>
                          {planFrequencies.length === 0 && row.menu_food_id
                            ? 'Sem periodicidade'
                            : 'Selecione'}
                        </option>
                        {planFrequencies.map(pf => {
                          const { periodicity, value } = parsePayamentForm(
                            pf.payament_form
                          )
                          return (
                            <option key={pf.id} value={pf.id}>
                              {pf.frequency} - {periodicity} (
                              {currencyMask(value)})
                            </option>
                          )
                        })}
                      </select>
                    </td>
                    <td className="col-md-2">
                      <input
                        type="text"
                        className="form-control form-control-solid fw-bold"
                        value={row.value}
                        onChange={e =>
                          handleRowChange(
                            index,
                            'value',
                            genericMaskWithTwoZero(e.target.value)
                          )
                        }
                        placeholder="0,00"
                      />
                    </td>
                    <td className="col-md-2">
                      <input
                        type="number"
                        min="1"
                        className="form-control form-control-solid fw-bold"
                        value={row.quantity}
                        onChange={e =>
                          handleRowChange(index, 'quantity', e.target.value)
                        }
                        placeholder="0"
                      />
                    </td>
                    <td>
                      {rows.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-icon btn-light-danger"
                          onClick={() => handleRemoveRow(index)}
                        >
                          <span className="fa fa-trash" />
                        </button>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={5} className="pt-0">
                      <input
                        type="text"
                        className="form-control form-control-solid form-control-sm"
                        value={row.observation}
                        onChange={e =>
                          handleRowChange(index, 'observation', e.target.value)
                        }
                        placeholder="Observação (opcional)"
                      />
                    </td>
                  </tr>
                </React.Fragment>
              )
            })}
          </tbody>
        </table>

        <div className="d-flex justify-content-between align-items-center mb-5">
          <a
            href="#"
            onClick={e => {
              e.preventDefault()
              handleAddRow()
            }}
            style={{ cursor: 'pointer' }}
          >
            <span className="fa fa-plus" /> Adicionar cardápio
          </a>
          <div className="fw-bolder fs-5">
            Total: {currencyMask(calculateTotal())}
          </div>
        </div>

        <div className="separator my-5" />

        <div className="mb-5">
          <label className="col-form-label fw-bold fs-6">
            Método de pagamento
          </label>
          <select
            className="form-select form-select-solid fw-bold"
            value={paymentMethod}
            onChange={e => setPaymentMethod(e.target.value)}
          >
            <option value="" disabled>
              Selecione
            </option>
            {PAYMENT_METHODS.map(pm => (
              <option key={pm.value} value={pm.value}>
                {pm.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-5">
          <label className="col-form-label fw-bold fs-6">
            Data de liquidação
          </label>
          <input
            type="text"
            className="form-control form-control-solid fw-bold"
            value={settlementDate}
            onChange={e => handleSettlementDateChange(e.target.value)}
            placeholder="dd/mm/aaaa"
            maxLength={10}
          />
        </div>

        <button
          type="button"
          className="btn btn-primary float-end"
          onClick={handleSubmit}
        >
          Criar movimentação financeira
        </button>
      </div>
    </>
  )
}

export default FormAddFinancialMoviment
